import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";

export default function GettingStartedPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Getting Started</h1>
          <p className="text-gray-200 mt-2">
            Everything you need to know to start using AirArrange
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* For Tenants */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Tenants</h2>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                1. Create Your Account
              </h3>
              <p className="text-gray-700 mb-3">
                Sign up for AirArrange using your email address or social media
                accounts. Complete your profile with business details to help
                hosts understand your needs.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Verify your email address</li>
                <li>Add your business information</li>
                <li>Upload a profile picture</li>
                <li>Set your notification preferences</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                2. Search for Workspaces
              </h3>
              <p className="text-gray-700 mb-3">
                Use our advanced search filters to find the perfect workspace for
                your business needs.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Filter by location, size, and amenities</li>
                <li>View photos and virtual tours</li>
                <li>Check availability calendars</li>
                <li>Read reviews from other tenants</li>
                <li>Compare multiple spaces</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                3. Make a Booking
              </h3>
              <p className="text-gray-700 mb-3">
                Once you find the right space, booking is simple and secure.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Select your desired dates</li>
                <li>Review pricing and terms</li>
                <li>Submit your booking request</li>
                <li>Wait for host approval</li>
                <li>Complete payment through our secure system</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                4. Communicate with Hosts
              </h3>
              <p className="text-gray-700 mb-3">
                Stay in touch with your host before, during, and after your stay.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Ask questions about the space</li>
                <li>Discuss special requirements</li>
                <li>Coordinate move-in details</li>
                <li>Report any issues promptly</li>
              </ul>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold mb-6">For Hosts</h2>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                1. List Your Workspace
              </h3>
              <p className="text-gray-700 mb-3">
                Create an attractive listing to showcase your workspace to
                potential tenants.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Provide detailed workspace information</li>
                <li>Upload high-quality photos</li>
                <li>Set competitive pricing</li>
                <li>Specify available amenities</li>
                <li>Define your house rules</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                2. Manage Bookings
              </h3>
              <p className="text-gray-700 mb-3">
                Review and approve booking requests from your dashboard.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Review tenant profiles and requests</li>
                <li>Approve or decline bookings</li>
                <li>Manage your availability calendar</li>
                <li>Set up automatic pricing rules</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                3. Prepare Your Space
              </h3>
              <p className="text-gray-700 mb-3">
                Ensure your workspace is ready for your tenant's arrival.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Clean and prepare the space</li>
                <li>Ensure all amenities are functional</li>
                <li>Provide access instructions</li>
                <li>Prepare any welcome materials</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                4. Get Paid
              </h3>
              <p className="text-gray-700 mb-3">
                Receive payments securely through our platform.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li>Set up your payment method</li>
                <li>Receive payments automatically</li>
                <li>Track your earnings</li>
                <li>Download financial reports</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
